'use client';
import React, { useState } from 'react';
import PublicNav from '@/components/PublicNav';
import FooterSection from '@/app/components/FooterSection';

import { MapPin, Phone, Mail, MessageCircle, Clock, Truck, ChevronRight } from 'lucide-react';

export default function ContactPage() {
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <div className="min-h-screen bg-background">
      <PublicNav />

      {/* Hero */}
      <section className="pt-24 pb-16 text-white" style={{ background: 'linear-gradient(160deg, #0f172a 0%, #0c4a6e 55%, #0369a1 100%)' }}>
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 xl:px-16 text-center">
          <span className="inline-block bg-white/10 text-sky-300 text-sm font-semibold px-4 py-1.5 rounded-full mb-4">
            Get in Touch
          </span>
          <h1 className="text-4xl md:text-5xl font-extrabold mb-4">Contact Us</h1>
          <p className="text-white/70 text-lg max-w-2xl mx-auto">
            Have a question, want to book, or need a custom quote? We are here to help. Reach us via WhatsApp, phone, or email.
          </p>
        </div>
      </section>

      {/* Contact Info + Form */}
      <section className="py-16 bg-white">
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 xl:px-16">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Details */}
            <div>
              <h2 className="text-2xl font-extrabold text-dark mb-6">Contact Information</h2>
              <div className="space-y-5 mb-8">
                <div className="flex items-start gap-4 p-5 bg-sky-50 rounded-2xl border border-sky-100">
                  <div className="w-10 h-10 rounded-xl bg-gradient-primary flex items-center justify-center flex-shrink-0">
                    <MapPin size={18} className="text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-dark text-sm mb-1">Location</p>
                    <p className="text-muted-foreground text-sm">Rooi Santu, Curaçao</p>
                    <p className="text-xs text-muted-foreground mt-1">Exact address will be confirmed soon</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-5 bg-sky-50 rounded-2xl border border-sky-100">
                  <div className="w-10 h-10 rounded-xl bg-green-500 flex items-center justify-center flex-shrink-0">
                    <MessageCircle size={18} className="text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-dark text-sm mb-1">WhatsApp (Preferred)</p>
                    <a href="https://wa.me/5999000000" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline text-sm font-semibold">
                      +599 9 000 0000 (placeholder)
                    </a>
                    <p className="text-xs text-muted-foreground mt-1">Fastest response — usually within 1 hour</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-5 bg-sky-50 rounded-2xl border border-sky-100">
                  <div className="w-10 h-10 rounded-xl bg-gradient-primary flex items-center justify-center flex-shrink-0">
                    <Phone size={18} className="text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-dark text-sm mb-1">Phone</p>
                    <p className="text-muted-foreground text-sm">+599 9 000 0000 (placeholder)</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-5 bg-sky-50 rounded-2xl border border-sky-100">
                  <div className="w-10 h-10 rounded-xl bg-gradient-primary flex items-center justify-center flex-shrink-0">
                    <Mail size={18} className="text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-dark text-sm mb-1">Email</p>
                    <a href="mailto:info@dripndrycuracao.com" className="text-primary hover:underline text-sm font-semibold">
                      info@dripndrycuracao.com (placeholder)
                    </a>
                  </div>
                </div>
              </div>

              {/* Hours */}
              <div className="bg-sky-50 rounded-2xl p-6 border border-sky-100">
                <h3 className="font-extrabold text-dark mb-4 flex items-center gap-2">
                  <Clock size={18} className="text-primary" />
                  Operating Hours
                </h3>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-gradient-primary flex items-center justify-center flex-shrink-0">
                      <MapPin size={14} className="text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-dark text-sm">Rooi Santu Location</p>
                      <p className="text-xs text-muted-foreground">Mon–Sat: 7:00 AM – 6:00 PM (placeholder)</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center flex-shrink-0">
                      <Truck size={14} className="text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-dark text-sm">Mobile Van — East Curaçao</p>
                      <p className="text-xs text-muted-foreground">7:00 AM – 12:00 PM</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center flex-shrink-0">
                      <Truck size={14} className="text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-dark text-sm">Mobile Van — West Curaçao</p>
                      <p className="text-xs text-muted-foreground">12:00 PM – 7:00 PM</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <h2 className="text-2xl font-extrabold text-dark mb-6">Send Us a Message</h2>
              <div className="bg-white rounded-3xl shadow-card border border-sky-100 p-8">
                {submitted ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 rounded-full bg-gradient-primary flex items-center justify-center mx-auto mb-4 shadow-glow">
                      <span className="text-white text-2xl">✓</span>
                    </div>
                    <h3 className="text-xl font-extrabold text-dark mb-2">Message Sent!</h3>
                    <p className="text-muted-foreground text-sm mb-4">Thank you for reaching out. We will get back to you within 1 business day.</p>
                    <a
                      href="https://wa.me/5999000000"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white font-semibold px-6 py-3 rounded-xl transition-colors text-sm"
                    >
                      <MessageCircle size={16} />
                      Or Chat on WhatsApp
                    </a>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-1.5">First Name</label>
                        <input
                          type="text"
                          className="w-full px-4 py-3 rounded-xl border border-input bg-sky-50/50 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                          placeholder="Keisha"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-1.5">Last Name</label>
                        <input
                          type="text"
                          className="w-full px-4 py-3 rounded-xl border border-input bg-sky-50/50 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                          placeholder="Martina"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-1.5">Email Address</label>
                      <input
                        type="email"
                        className="w-full px-4 py-3 rounded-xl border border-input bg-sky-50/50 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                        placeholder="keisha@email.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-1.5">Phone / WhatsApp</label>
                      <input
                        type="tel"
                        className="w-full px-4 py-3 rounded-xl border border-input bg-sky-50/50 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                        placeholder="+599 9 000 0000"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-1.5">Subject</label>
                      <select className="w-full px-4 py-3 rounded-xl border border-input bg-sky-50/50 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary">
                        <option value="">Select a topic...</option>
                        <option value="booking">Booking Inquiry</option>
                        <option value="subscription">Subscription Question</option>
                        <option value="loyalty">Loyalty Program</option>
                        <option value="fleet">Fleet / Company Account</option>
                        <option value="complaint">Complaint or Feedback</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-1.5">Message</label>
                      <textarea
                        rows={4}
                        className="w-full px-4 py-3 rounded-xl border border-input bg-sky-50/50 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary resize-none"
                        placeholder="How can we help you?"
                      />
                    </div>
                    <button
                      type="submit"
                      className="btn-primary w-full justify-center py-3.5 text-sm"
                    >
                      Send Message
                      <ChevronRight size={16} />
                    </button>
                    <p className="text-xs text-muted-foreground text-center">
                      For fastest response, contact us directly on{' '}
                      <a href="https://wa.me/5999000000" target="_blank" rel="noopener noreferrer" className="text-green-600 font-semibold hover:underline">WhatsApp</a>.
                    </p>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WhatsApp CTA */}
      <section className="py-12 bg-green-500">
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 xl:px-16 text-center">
          <h2 className="text-2xl font-extrabold text-white mb-3">Fastest Way to Reach Us</h2>
          <p className="text-white/80 mb-6">Send us a WhatsApp message and we will respond within the hour.</p>
          <a
            href="https://wa.me/5999000000?text=Hi%20Drip%20N%20Dry%20Cura%C3%A7ao%2C%20I%20have%20a%20question"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-white text-green-600 font-extrabold px-8 py-4 rounded-xl hover:bg-green-50 transition-colors text-base shadow-lg"
          >
            <MessageCircle size={20} />
            Open WhatsApp Chat
          </a>
        </div>
      </section>

      <FooterSection />
    </div>
  );
}
